// components/organism/ForgotPassword/ForgotPasswordForm.tsx
import React, { type FormEvent, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { z } from 'zod';
import { Button, Input, Label } from 'shared-portal-components-v2';

const FIRST_ERROR_INDEX = 0;

const forgotPasswordSchema = z.object({
  emailAddress: z.string().email('Please enter a valid email address').nonempty('Email is required'),
});

export interface ForgotPasswordFormProps {
  onSubmit: (values: { emailAddress: string }) => void | Promise<void>;
  isLoading?: boolean;
  error?: string;
}

export default function ForgotPasswordForm({
  onSubmit,
  isLoading = false,
  error,
}: ForgotPasswordFormProps) {
  const [emailAddress, setEmailAddress] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const parsed = useMemo(
    () => forgotPasswordSchema.safeParse({ emailAddress: emailAddress.trim() }),
    [emailAddress],
  );

  const emailError = useMemo(() => {
    if (!hasSubmitted || parsed.success) return undefined;
    return parsed.error.flatten().fieldErrors.emailAddress?.[FIRST_ERROR_INDEX];
  }, [hasSubmitted, parsed]);

  const isContinueDisabled = isLoading || (hasSubmitted && !parsed.success);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setHasSubmitted(true);
    if (!parsed.success) return;
    void onSubmit({ emailAddress: parsed.data.emailAddress });
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setEmailAddress(event.target.value);
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      {error !== undefined && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="forgot-email" isRequired className="text-base">
            Email address
          </Label>
          <Input
            id="forgot-email"
            type="email"
            required
            placeholder="Enter your email address"
            value={emailAddress}
            onChange={handleEmailChange}
            variant="outline"
            inputClassName="h-12 text-base sm:text-sm"
            error={emailError}
          />
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full h-12"
        isLoading={isLoading}
        loadingText="Sending..."
        disabled={isContinueDisabled}
      >
        Send Reset Link
      </Button>

      <div className="flex justify-center pt-2">
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to sign in
        </Link>
      </div>
    </form>
  );
}
